import type { ArticleData, ArticleDFPTags } from '../entities/article';
import type { StoryCategory } from '@benzinga/advanced-news-manager';
import { ArticleVideoPlayer } from '@benzinga/content-manager';
import { runningClientSide } from '@benzinga/utils';
import { formatDFPTagCollection } from '@benzinga/utils';
import {
  isZacksAppliesToTickers,
  ZACK_DFP_SLUG,
  getPrimisIDbyChannel,
} from '@benzinga/ads-utils';
import { MetaProps } from '@benzinga/seo';
import { isMobile } from '@benzinga/device-utils';
import cboeKeywordsList from '../assets/keywords/cboe';
import tradestationKeywordsList from '../assets/keywords/tradestation';
import rocketMortgageKeywordsList from '../assets/keywords/rocketMortgage';
import { getSovPosition } from './ads-taboola';

type TargetingElement = string | number;

export const attachThirdPartyDFPTargeting = async (
  article: ArticleData,
  targeting: ArticleDFPTags,
): Promise<ArticleDFPTags> => {
  if (!Array.isArray(article.tickers)) {
    return targeting;
  }

  const tickers = article.tickers.map(ticker => ticker.name);
  const isZacksApplied = await isZacksAppliesToTickers(tickers);
  if (isZacksApplied) {
    Array.isArray(targeting.BZ_TAG) && targeting.BZ_TAG.push(ZACK_DFP_SLUG);
  }

  return targeting;
};

export const setDFPTargeting = (article: ArticleData) => {
  if (!article || !runningClientSide()) return;

  const targeting = getDFPTargeting(article);
  if (!targeting) return;

  //DFPManager.setTargetingArguments(targeting);
  //DFPManager.load();
};

export const getDFPTargeting = async (article: ArticleData): Promise<ArticleDFPTags | null> => {
  if (!article) return null;
  const channelTargeting: TargetingElement[] = formatDFPTagCollection<StoryCategory>(article.channels);
  const tagTargeting: TargetingElement[] = formatDFPTagCollection<StoryCategory>(article.tags);
  const tickersTargeting: TargetingElement[] = formatDFPTagCollection<StoryCategory>(article.tickers);

  let targeting: ArticleDFPTags = {
    BZ_AUTHOR: article.name ?? null,
    BZ_CHANNEL: channelTargeting ?? null,
    BZ_NID: `${article.nodeId}`,
    BZ_PTYPE: article.type || article.metaProps?.pageType || undefined,
    BZ_TAG: tagTargeting ?? null,
    BZ_TICKER: tickersTargeting ?? null,
    ...(article.dfpTags || {}),
  };

  const targetingWithThirdPartyDTFTagsAttrached = await attachThirdPartyDFPTargeting(article, targeting)
    .then(newTargeting => {
      targeting = newTargeting;
      return targeting;
    })
    .catch(error => {
      console.log(`Error attaching third party targeting on article: ${article.nodeId} -`, error);
      return targeting;
    });

  return targetingWithThirdPartyDTFTagsAttrached;
};

export const getArticleVideoPlayerSOV = (videoPlayerSettings: ArticleVideoPlayer, playersKeys: string[]): string => {
  const VIDEO_PLAYER_SOV_SLUG = 'videoPlayerSOVSlug';
  let videoPlayerSOVSlug = '';
  if (videoPlayerSettings?.connatix) {
    videoPlayerSOVSlug = 'connatix';
  } else {
    videoPlayerSOVSlug = window[VIDEO_PLAYER_SOV_SLUG] || getSovPosition(playersKeys, [10, 90]);
  }

  return videoPlayerSOVSlug;
};

export const setArticleVideoPlayerSOV = (videoPlayerSlug: string) => {
  const VIDEO_PLAYER_SOV_SLUG = 'videoPlayerSOVSlug';
  window[VIDEO_PLAYER_SOV_SLUG] = videoPlayerSlug;
};

export const getPrimisIdByTaxonomies = (channels: StoryCategory[], tags: StoryCategory[]): string => {
  const taxonomies: StoryCategory[] = [];

  if (Array.isArray(channels)) {
    taxonomies.push(...channels);
  }

  if (Array.isArray(tags)) {
    taxonomies.push(...tags);
  }

  const taxonomiesNames = taxonomies.map(item => item.name);

  return getPrimisIDbyChannel(taxonomiesNames);
};

export const getSpecificArticlesPrimisID = (articleId: number): string | null => {
  const DIREXION_IDS = {
    desktop: '116214',
    mobile: '116215',
  };

  const DATAWRKZ_IDS = {
    desktop: '116239',
    mobile: '116240',
  };

  const articles = {
    34423367: DIREXION_IDS,
    34444357: DIREXION_IDS,
    34449307: DIREXION_IDS,
    34502231: DATAWRKZ_IDS,
  };

  const targetId = runningClientSide() && isMobile() ? articles[articleId]?.mobile : articles[articleId]?.desktop;

  return targetId || null;
};

export const getArticleKeywordsGroup = (articleBody: string): { positive: string[]; negative: string[] } => {
  const groups = {
    cboe: {
      keys: cboeKeywordsList,
      name: 'CBOE',
      type: 'positive',
    },
    rocketMortgage: {
      keys: rocketMortgageKeywordsList,
      name: 'Rocket Mortgage',
      type: 'negative',
    },
    tradestation: {
      keys: tradestationKeywordsList,
      name: 'Tradestation',
      type: 'positive',
    },
  };

  const chosenGroups: { positive: string[]; negative: string[] } = { negative: [], positive: [] };
  for (const keyGroup in groups) {
    const matched = matchKeywords(groups[keyGroup].keys, articleBody);

    if (matched) {
      if (groups[keyGroup].type === 'positive') {
        chosenGroups.positive.push(groups[keyGroup].name);
      } else {
        chosenGroups.negative.push(groups[keyGroup].name);
      }
    }
  }

  return chosenGroups;
};

export const matchKeywords = (keywords: string[], articleBody: string): boolean => {
  const matches: string[] = [];
  if (Array.isArray(keywords) && articleBody) {
    for (const keyword of keywords) {
      const regex = new RegExp(`\\b${keyword.toLowerCase()}\\b`);
      if (articleBody.toLowerCase().match(regex)) {
        matches.push(keyword);
      }
    }
  }
  return !!matches.length;
};

export const injectVendorInArticleUrls = (referrer?: string) => {
  if (runningClientSide()) {
    const targetRef = referrer || document?.referrer;
    const { utm_source } = getArticleCampaignQueryParams();

    const targetSelector = targetRef || utm_source;

    if (targetSelector) {
      const allowedRefs = ['newsbreak.com', 'newsbreakapp.com'];

      const allowed = allowedRefs.some(ref => targetSelector.toLowerCase().includes(ref));

      if (allowed) {
        const links = document.querySelectorAll('.article-layout-wrapper a, .layout-sidebar a');

        if (links) {
          injectUtmSourceInUrls(links as NodeListOf<HTMLAnchorElement>, targetSelector);
        }
      }
    }
  }
};

export const injectUtmSourceInUrls = (urlsElements: NodeListOf<HTMLAnchorElement>, utmSource: string): void => {
  if (urlsElements?.length) {
    urlsElements.forEach((item: HTMLAnchorElement) => {
      if (item.href.includes('benzinga.com') && !item.href.includes('utm_source')) {
        item.href += item.href.includes('?') ? `&utm_source=${utmSource}` : `?utm_source=${utmSource}`;
      }
    });
  }
};

export const getArticleCampaignQueryParams = (): Record<string, string | null> => {
  if (!runningClientSide() || !document?.location) {
    return {};
  }

  const params = new URL(`${document.location}`).searchParams;

  return {
    campaigns_strategy: params.get('campaigns_strategy'),
    utm_source: params.get('utm_source'),
  };
};

export const getArticleReferer = (articleMeta?: MetaProps): string | undefined | null => {
  if (!runningClientSide()) {
    return articleMeta?.referer;
  }

  return document?.referrer;
};

const CAMPAIGN_REF_STORAGE_KEY = 'campaign-referrer';

export const setCampaignSessionReferrer = (referrer?: string): void => {
  if (runningClientSide()) {
    const targetRef = referrer || document?.referrer;
    if (targetRef) {
      const allowedRefs = ['newsbreak.com', 'newsbreakapp.com'];

      const allowed = allowedRefs.some(ref => targetRef.toLowerCase().includes(ref));

      if (allowed) {
        window.sessionStorage.setItem(CAMPAIGN_REF_STORAGE_KEY, targetRef);
      }
    }
  }
};

export const getCampaignSessionReferrer = () => {
  if (runningClientSide()) {
    return window.sessionStorage.getItem(CAMPAIGN_REF_STORAGE_KEY);
  }

  return null;
};
